import { FeatureFlag } from 'controllers/_exports';
import { useMemo } from 'react';
import { useMultipleChoice } from './useMultipleChoice';
import { useGetFF } from '@fstack-tech/ff_react_module';
import { useImport } from './useImport';

const baseFeedbackContentTypes = [
  {
    value: '6',
    label: 'Text (1000 words)',
    title: 'Text',
    platforms: [],
    extensions: [],
    icon: 'txt.svg',
  },
  {
    value: '1',
    label: 'Video (.mp4, .mov)',
    platforms: [
      'youtube.com',
      'youtu.be',
      'loom.com',
      'vimeo.com',
      'video/quicktime',
      'video/mp4',
      'video/ogg',
    ],
    extensions: [
      '.WEBM',
      '.MPG',
      '.MP2',
      '.MPEG',
      '.MPE',
      '.MPV',
      '.OGG',
      '.MP4',
      '.M4P',
      '.M4V',
      '.MOV',
      '.QT',
      '.AVI',
      '.MKV',
    ],
    title: 'Video',
    icon: 'video.svg',
  },
  {
    value: '2',
    label: 'Audio (.mp3)',
    platforms: [
      'podcast.co',
      'soundtrap.com',
      'soundcloud.com',
      'anchor.fm',
      'audio/mpeg',
      'audio/wav',
      'audio/mp3',
      'audio/mp4',
    ],
    extensions: ['.mp3', '.mp4', '.wav'],
    title: 'Audio',
    icon: 'audio.svg',
  },
  {
    value: '3',
    label: 'Document (.pdf, .docx)',
    platforms: ['docs', 'document', 'application/pdf', 'officedocument'],
    extensions: ['.docx', '.odt', '.rtf', '.pdf'],
    title: 'Document',
    icon: 'word.svg',
  },
  {
    value: '4',
    label: 'Spreadsheet (.csv, .xls)',
    platforms: ['spreadsheet', 'text/csv', 'text/xls', 'text/ods', 'text/tsv', 'text/xlsx'],
    extensions: ['.xls', '.csv', '.ods', '.tsv', '.xlsx'],
    title: 'Spreadsheet',
    icon: 'xls.svg',
  },
  {
    value: '5',
    label: 'Presentation (.pptx, .jpg, .png)',
    platforms: ['presentation', 'image/png', 'image/jpeg', 'image/jpg', 'image/svg'],
    extensions: ['.pptx', '.jpg', '.jpeg', '.png', '.svg'],
    title: 'Presentation',
    icon: 'ppt.svg',
  },
];

const getFeedbackContentTypes = ({ ff_bulk_mcq, includeImport = true }) => {
  const _types = [...baseFeedbackContentTypes];
  if (FeatureFlag.enabled('ASSESSMENT_URL'))
    _types.push(
      ...[
        {
          value: '7',
          label: 'Video recording (max 5 mins)',
          extensions: [],
          title: 'Video recording',
          icon: 'video.svg',
        },
        {
          value: '8',
          label: 'Link (URL)',
          platforms: [],
          extensions: ['URL'],
          title: 'Link',
          icon: 'txt.svg',
        },
      ]
    );
  if (FeatureFlag.enabled('ASSESSMENT_MULTIPLE'))
    _types.push({
      value: '9',
      label: 'Multiple choice',
      extensions: [],
      title: 'Multiple choice',
      icon: 'txt.svg',
    });
  if (ff_bulk_mcq && includeImport)
    _types.push({
      value: '10',
      label: 'Import',
      extensions: [],
      title: 'Import',
      icon: 'txt.svg',
    });
  return _types;
};

const useAssessment = (ff_group_by_topic) => {
  const multipleChoice = useMultipleChoice();
  const isImportMCQ = useImport(ff_group_by_topic);
  return { ...multipleChoice, ...isImportMCQ };
};

export const useGetFeedbackContentTypes = ({ includeImport = true } = {}) => {
  const { isEnabled: ff_bulk_mcq } = useGetFF({ flag_id: 'bulk_mcq' });
  const feedbackContentTypes = useMemo(
    () => getFeedbackContentTypes({ ff_bulk_mcq, includeImport }),
    [ff_bulk_mcq, includeImport]
  );

  return feedbackContentTypes;
};

export default useAssessment;
